import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  X,
  Edit,
  Trash,
  Paperclip,
  Calendar,
  CheckCircle,
  Users,
  Award,
  ClipboardCheck,
  FileText,
  ArrowLeft,
  Clock,
  AlertCircle,
  BookOpen,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AssignmentSectionRevamp from "./CreateAssignmentNew";
import EditAssignmentForm from "./EditAssignment";
import { Link, useNavigate } from "react-router-dom";
import { useCourse } from "../../../context/CourseContext";
import {
  deleteAssignment,
  getAllCourseAssignments,
} from "../../../services/assignment.service";
import toast from "react-hot-toast";
import { cn } from "../../../lib/utils";
import { getCardColor } from "../../../utils/courseHelpers";
import {
  card,
  cardInteractive,
  surfaceMuted,
  emptyState,
  pageTitle,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  badgeBase,
  dividerInner,
  iconSize,
  motionFadeUp,
  motionStaggerChild,
  motionHoverLift,
} from "../../../lib/design-system";

// Shared button constants
const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecondary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        {...motionFadeUp}
        className={cn(
          card,
          "relative w-full max-w-7xl max-h-[95vh] flex flex-col shadow-xl mx-4"
        )}
      >
        <div
          className={cn(
            "px-5 pt-5 pb-4 border-b sticky top-0 bg-white dark:bg-gray-800 z-10 flex items-center justify-between",
            dividerInner
          )}
        >
          <h2 className={cardTitle}>{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
          >
            <X className={cn(iconSize.md, "text-gray-500 dark:text-gray-400")} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </motion.div>
    </div>,
    document.body
  );
};

// ─── Helper: due date status ──────────────────────────────────────────────────
const getDueStatus = (dueDateStr) => {
  if (!dueDateStr) return null;
  const now = new Date();
  const due = new Date(dueDateStr);
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return {
      label: "Overdue",
      color: "text-red-600 dark:text-red-400",
      bg: "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700",
    };
  if (diffDays === 0)
    return {
      label: "Due today",
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700",
    };
  if (diffDays <= 3)
    return {
      label: `${diffDays}d left`,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700",
    };
  return {
    label: `${diffDays}d left`,
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700",
  };
};

// ─── Helper: format date short ────────────────────────────────────────────────
const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// ─── Assignment Card (Grid) ──────────────────────────────────────────────────
const AssignmentCard = ({ assignment, onEdit, onDelete, onGrade, index }) => {
  const {
    id,
    title,
    description,
    stats,
    attachments,
    isActive,
    branches,
    originalDueDate,
    questionStats,
    isUngraded,
    totalPoints,
    completeIn,
  } = assignment;

  const cardColor = getCardColor(title || id);
  const dueStatus = getDueStatus(originalDueDate);
  const turnedInPct =
    stats?.assigned > 0
      ? Math.round(((stats?.turnedIn || 0) / stats.assigned) * 100)
      : 0;
  const questionCount =
    questionStats?.totalQuestions || assignment.questions?.length || 0;

  return (
    <motion.div
      {...motionStaggerChild(index)}
      whileHover={motionHoverLift}
      className="group"
    >
      <div className={cn(cardInteractive, "flex flex-col h-full")}>
        {/* ── Color Banner with Title ──────────────────────────── */}
        <Link to={`/teacher/assignment/${id}`} className="block">
          <div
            className="relative h-28 sm:h-32 w-full overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor} 60%, ${cardColor}cc 60%, ${cardColor}99 100%)`,
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)",
              }}
            />

            {/* Status badge — top-right, high contrast */}
            <span
              className={cn(
                "absolute top-2.5 right-2.5 px-2.5 py-0.5 text-xs font-medium rounded-sm",
                isActive
                  ? "bg-white text-emerald-600"
                  : "bg-white text-red-600"
              )}
            >
              {isActive ? "Active" : "Closed"}
            </span>

            {/* Points badge — top-left */}
            {isUngraded ? (
              <span className="absolute top-2.5 left-3 px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-sm border border-white/10">
                Ungraded
              </span>
            ) : (
              <span className="absolute top-2.5 left-3 px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-sm border border-white/10">
                {totalPoints} pts
              </span>
            )}

            {/* Title + Description — bottom of banner */}
            <div className="absolute bottom-2 left-3 right-3">
              <h3 className="text-white text-lg sm:text-xl tracking-wide leading-snug line-clamp-1 drop-shadow-sm">
                {title}
              </h3>
              {description && (
                <p className="text-white/70 text-xs sm:text-sm mt-0.5 line-clamp-1 drop-shadow-sm">
                  {description.length > 60 ? description.slice(0, 60) + "..." : description}
                </p>
              )}
            </div>
          </div>
        </Link>

        {/* ── Body ─────────────────────────────────────────────── */}
        <div className="px-3 sm:px-4 py-2">
          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            {originalDueDate && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateShort(originalDueDate)}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {questionCount} Q
            </span>
            {completeIn && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {completeIn}m
              </span>
            )}
            {attachments && attachments.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                {attachments.length}
              </span>
            )}
            {branches &&
              branches.length > 0 &&
              !branches.includes("ALL") && (
                <span className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400">
                  {branches.join(", ")}
                </span>
              )}
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        <div
          className="px-3 sm:px-4 py-2"
        >
          {/* Submission progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {stats?.turnedIn || 0}
              </span>
              /{stats?.assigned || 0} submitted
            </span>
            <span className="text-[11px] font-bold text-gray-600 dark:text-gray-300 tabular-nums">
              {turnedInPct}%
            </span>
          </div>
          <div className="w-full h-1 bg-gray-100 dark:bg-gray-700 rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm transition-all duration-500"
              style={{
                width: `${turnedInPct}%`,
                backgroundColor: cardColor,
              }}
            />
          </div>

          {/* Action buttons — all labeled, evenly spaced */}
          <div className="flex items-center gap-1.5 mt-2.5">
            <button
              onClick={() => onGrade(id)}
              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-xs font-medium text-white bg-accent1 hover:bg-accent1/90 transition-colors"
            >
              <ClipboardCheck className="h-3.5 w-3.5" />
              Grade
            </button>
            <button
              onClick={() => onEdit(id)}
              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Edit className="h-3.5 w-3.5" />
              Edit
            </button>
            <button
              onClick={() => onDelete(id)}
              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              <Trash className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AllAssignments = ({
  courseID,
  initialTab = "subjective",
  hideTabs = false,
}) => {
  const navigate = useNavigate();
  const { courseData } = useCourse();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [createAssignmentType, setCreateAssignmentType] = useState(initialTab);

  const lockedTab = hideTabs ? initialTab : null;

  // ─── Fetch ─────────────────────────────────────────────────────────
  const fetchAssignments = async () => {
    if (!courseID) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await getAllCourseAssignments({ courseID });
      const courseAssignments = response.assignments || [];

      const formattedAssignments = courseAssignments.map((assignment) => {
        const questions = assignment.questions || [];
        const hasSubjective = questions.some((q) => q.type === "subjective");
        const hasObjective = questions.some((q) => q.type === "objective");

        let assignmentType = "subjective";
        if (hasObjective && !hasSubjective) assignmentType = "objective";
        else if (hasSubjective && !hasObjective) assignmentType = "subjective";
        else if (questions.length > 0)
          assignmentType = questions[0].type || "subjective";

        return {
          id: assignment._id,
          title: assignment.title,
          description: assignment.description,
          courseId: assignment.course,
          dueDate: assignment.dueDate
            ? `Due ${new Date(assignment.dueDate).toLocaleDateString()}`
            : "",
          originalDueDate: assignment.dueDate,
          date: assignment.createdAt
            ? `Posted ${new Date(assignment.createdAt).toLocaleDateString()}`
            : "",
          stats: {
            turnedIn: assignment.stats?.turnedIn || 0,
            assigned:
              assignment.stats?.assigned || assignment.stats?.total || 0,
            graded: assignment.stats?.graded || 0,
          },
          attachments: assignment.attachments || [],
          grade: assignment.totalPoints || 100,
          totalPoints: assignment.totalPoints || 100,
          isUngraded: assignment.isUngraded || false,
          completeIn: assignment.completeIn || null,
          allowLateSubmissions: assignment.allowLateSubmissions !== false,
          topic: assignment.description
            ? assignment.description.substring(0, 20) + "..."
            : "N/A",
          isActive: assignment.isActive !== false,
          assignmentType,
          questions,
          questionStats: assignment.questionStats || null,
          branches: assignment.branches || [],
        };
      });

      setAllAssignments(formattedAssignments);

      const filtered = formattedAssignments.filter((a) => {
        if (activeTab === "subjective")
          return a.questions?.some((q) => q.type === "subjective");
        return a.questions?.some((q) => q.type === "objective");
      });

      setAssignments(filtered);
    } catch (err) {
      console.error("Error fetching assignments:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to load assignments. Please try again later.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (courseID) fetchAssignments();
  }, [courseID]);

  useEffect(() => {
    if (allAssignments.length > 0) {
      const filterTab = lockedTab || activeTab;
      const filtered = allAssignments.filter((a) => {
        if (filterTab === "subjective")
          return a.questions?.some((q) => q.type === "subjective");
        return a.questions?.some((q) => q.type === "objective");
      });
      setAssignments(filtered);
    }
  }, [activeTab, allAssignments, lockedTab]);

  // ─── Handlers ──────────────────────────────────────────────────────
  const handleOpenCreateModal = (type = null) => {
    setIsCreateModalOpen(true);
    setCreateAssignmentType(type || activeTab);
  };

  const handleOpenEditModal = (assignment) => {
    setCurrentAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const handleEdit = (assignmentId) => {
    const a = assignments.find((x) => x.id === assignmentId);
    if (a) handleOpenEditModal(a);
  };

  const handleDelete = async (assignmentId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this assignment? This action cannot be undone."
      )
    )
      return;
    try {
      await deleteAssignment(assignmentId);
      await fetchAssignments();
    } catch (error) {
      console.error("Error deleting assignment:", error);
    }
  };

  const handleGrade = (assignmentId) => {
    const currentTab = hideTabs ? initialTab : activeTab;
    const currentSection =
      currentTab === "subjective" ? "Subjective" : "Objective";
    if (courseID) {
      localStorage.setItem(
        `course_${courseID}_selectedSection`,
        currentSection
      );
    }
    navigate(`/teacher/assignment/${assignmentId}/grade`);
  };

  // ─── Loading skeleton (header + grid) ────────────────────────────────
  if (isLoading) {
    return (
      <div className={cn(surfaceMuted)}>
        {/* Header skeleton */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 animate-pulse">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-7 w-40 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              <div className="h-1 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm mt-2" />
            </div>
            <div className="h-9 w-28 bg-gray-200 dark:bg-gray-700 rounded-sm" />
          </div>
          <div className="flex gap-4 mt-5">
            <div className="h-8 w-28 bg-gray-100 dark:bg-gray-800 rounded-sm" />
            <div className="h-8 w-28 bg-gray-100 dark:bg-gray-800 rounded-sm" />
          </div>
        </div>

        {/* Card grid skeleton */}
        <div className="p-3 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={cn(card, "overflow-hidden")}>
                <div className="h-28 sm:h-32 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="px-3 sm:px-4 py-2">
                  <div className="flex gap-3 animate-pulse">
                    <div className="h-3 w-14 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                    <div className="h-3 w-10 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                    <div className="h-3 w-12 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                  </div>
                </div>
                <div className="px-3 sm:px-4 py-2 animate-pulse">
                  <div className="flex items-center justify-between mb-2">
                    <div className="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                    <div className="h-3 w-8 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                  </div>
                  <div className="h-1 w-full bg-gray-100 dark:bg-gray-800 rounded-sm" />
                  <div className="flex gap-1.5 mt-2.5">
                    <div className="flex-1 h-7 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                    <div className="flex-1 h-7 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                    <div className="flex-1 h-7 bg-gray-100 dark:bg-gray-800 rounded-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 p-4 rounded-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              Failed to load assignments
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const subjectiveCount = allAssignments.filter((a) =>
    a.questions?.some((q) => q.type === "subjective")
  ).length;
  const objectiveCount = allAssignments.filter((a) =>
    a.questions?.some((q) => q.type === "objective")
  ).length;
  const displayTab = lockedTab || activeTab;

  const handleBackToContinuousAssessment = () => {
    if (courseID) {
      const sectionName = "Continuous Assessment";
      localStorage.setItem(`course_${courseID}_selectedSection`, sectionName);
      window.dispatchEvent(
        new CustomEvent("sectionChange", {
          detail: { section: sectionName },
        })
      );
    }
  };

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div className={cn(surfaceMuted, "min-h-[400px]")}>
      {/* ── Header ────────────────────────────────────────────── */}
      {!isCreateModalOpen && (
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          {hideTabs && (
            <button
              onClick={handleBackToContinuousAssessment}
              className={cn(btnSecondary, "mb-3 -ml-1 text-xs py-1.5")}
            >
              <ArrowLeft className={iconSize.md} />
              <span className="hidden sm:inline">
                Back to Continuous Assessment
              </span>
              <span className="sm:hidden">Back</span>
            </button>
          )}

          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className={pageTitle}>Assignments</h2>
              <div className={cn(accentBarSection, "mt-2")} />
            </div>
            <button
              className={cn(btnPrimary, "text-xs sm:text-sm px-3 sm:px-4")}
              onClick={() => handleOpenCreateModal(displayTab)}
            >
              <Plus className={iconSize.md} />
              <span className="hidden sm:inline">New Assignment</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>

          {/* ── Tab strip ──────────────────────────────────────── */}
          {!hideTabs && (
            <div
              className={cn(
                "flex mt-5 -mx-4 sm:-mx-6 px-4 sm:px-6"
              )}
            >
              {[
                {
                  key: "subjective",
                  icon: FileText,
                  label: "Subjective",
                  count: subjectiveCount,
                },
                {
                  key: "objective",
                  icon: CheckCircle,
                  label: "Objective",
                  count: objectiveCount,
                },
              ].map((tab) => {
                const isActive_ = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors",
                      isActive_
                        ? "text-accent1"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    )}
                  >
                    <tab.icon className="h-3.5 w-3.5" />
                    <span>{tab.label}</span>
                    <span
                      className={cn(
                        "ml-1 px-1.5 py-0.5 text-xs rounded-sm font-bold tabular-nums",
                        isActive_
                          ? "bg-accent1/10 text-accent1"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                      )}
                    >
                      {tab.count}
                    </span>

                    {isActive_ && (
                      <motion.div
                        layoutId="activeAssignmentTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent1"
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Create Assignment Form ────────────────────────────── */}
      {isCreateModalOpen && (
        <div className={cn(surfaceMuted)}>
          <AssignmentSectionRevamp
            courseID={courseID}
            inModal={false}
            assignmentType={createAssignmentType}
            onSave={(data) => {
              console.log("Assignment saved:", data);
              fetchAssignments();
              setIsCreateModalOpen(false);
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </div>
      )}

      {/* ── Edit Modal ────────────────────────────────────────── */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Assignment"
      >
        {currentAssignment && (
          <EditAssignmentForm
            assignment={currentAssignment}
            courseID={courseID}
            onClose={() => setIsEditModalOpen(false)}
            fetchAssignments={fetchAssignments}
            courseData={courseData}
          />
        )}
      </Modal>

      {/* ── Assignment Grid ────────────────────────────────────── */}
      {!isCreateModalOpen && (
        <div className="p-3 sm:p-5">
          <AnimatePresence mode="wait">
            {assignments.length === 0 ? (
              <motion.div
                key="empty"
                {...motionFadeUp}
                className={emptyState}
              >
                <BookOpen
                  className={cn(
                    iconSize.empty,
                    "mx-auto text-gray-300 dark:text-gray-600 mb-3"
                  )}
                />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                  No {displayTab} assignments yet
                </p>
                <p className={cn(bodySecondary, "mb-4")}>
                  Create your first assignment to get started
                </p>
                <button
                  className={cn(btnPrimary)}
                  onClick={() => handleOpenCreateModal(displayTab)}
                >
                  <Plus className={iconSize.md} />
                  Create Assignment
                </button>
              </motion.div>
            ) : (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {assignments.map((assignment, index) => (
                  <AssignmentCard
                    key={assignment.id}
                    assignment={assignment}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onGrade={handleGrade}
                    index={index}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default AllAssignments;
